"use client";

import { useRouter } from "next-nprogress-bar";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import {
  type ProductAnnouncement,
  ProductAnnouncementCategoryEnum,
} from "@/generated/email_parser.openapi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CloseButton } from "@/components/ui/BackButton";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { convertUTCToLocalDateIgnoringTimezone } from "@/lib/utils";

type Props = {
  announcements: ProductAnnouncement[];
  open: boolean;
  teaserVisible: boolean;
  onOpen: () => void;
  onOpenChange: (open: boolean) => void;
  onDismissTeaser: () => void;
};

/**
 * Frame shape used until the artwork reports its own dimensions. Close to the
 * ratios we actually publish, so the settle is small rather than a jolt.
 */
const RESERVED_MEDIA_ASPECT_RATIO = 2;

/** Guards against a degenerate asset producing an absurdly tall or wide frame. */
const MIN_MEDIA_ASPECT_RATIO = 0.4;
const MAX_MEDIA_ASPECT_RATIO = 5;

/**
 * An asset whose intrinsic box fits inside this is a UI icon, not editorial
 * artwork, however square or wide it happens to be.
 *
 * Shape alone can't tell the two apart: a 1000px square photo is legitimate
 * artwork and should fill the frame. Intrinsic size can. The Gondola Cash
 * release published `gondola-cash.svg` — a 32x32 icon — as its image, and the
 * shape-driven frame dutifully reserved a square capped at `max-h-96`, then
 * upscaled 32px of art into a 384px block. That pushed the body copy and the
 * "Read more" control that reveals it under the floating Next pill, so the
 * release rendered as a giant icon and nothing else.
 */
const ICON_MAX_INTRINSIC_PX = 256;

/**
 * A section counts as active once its top clears this offset from the top of
 * the viewport, matching where the eye lands while scrolling. Must stay in
 * sync with the mobile surface:
 * gondola-mobile-app/components/features/product-announcements/ProductAnnouncementsSurface.tsx
 */
const SECTION_ACTIVATION_OFFSET_PX = 160;

/**
 * Ratios measured this session, keyed by media URL.
 *
 * Radix unmounts the dialog subtree on close, so component state alone would
 * re-reserve `RESERVED_MEDIA_ASPECT_RATIO` and settle again on *every* open, not
 * once. Memoising outside the component means only the first view of an image
 * ever settles.
 */
const measuredMedia = new Map<string, MediaMeasurement>();

type MediaMeasurement = {
  aspectRatio: number;
  /** See ICON_MAX_INTRINSIC_PX. */
  isIcon: boolean;
};

/**
 * The frame measures the artwork, but the body copy needs the answer too (an
 * icon-only release opens expanded). They are siblings, and the older releases
 * render inside a `.map()` where shared state cannot be a hook — so the memo
 * that already had to be module state doubles as the store both read from.
 */
const measurementListeners = new Set<() => void>();

function recordMeasurement(mediaUrl: string, measurement: MediaMeasurement) {
  measuredMedia.set(mediaUrl, measurement);
  measurementListeners.forEach((listener) => listener());
}

// Hoisted rather than inlined into the hook: an inline closure gets a new
// identity every render, and useSyncExternalStore resubscribes whenever
// `subscribe` changes. This one captures nothing but module state.
function subscribeToMeasurements(onStoreChange: () => void): () => void {
  measurementListeners.add(onStoreChange);
  return () => {
    measurementListeners.delete(onStoreChange);
  };
}

function useMediaMeasurement(mediaUrl: string): MediaMeasurement | null {
  // `get` returns the one stored object, so the snapshot is reference-stable
  // between notifications — what useSyncExternalStore requires.
  const read = () => measuredMedia.get(mediaUrl) ?? null;
  return useSyncExternalStore(subscribeToMeasurements, read, read);
}

/**
 * Test-only. The memo above is module state, so it survives between tests in a
 * file and one test firing `load` would otherwise change what a later test sees
 * reserved. Call this in `beforeEach` rather than relying on every test to
 * invent a unique `mediaUrl`.
 */
export function clearMeasuredAspectRatiosForTests(): void {
  measuredMedia.clear();
  measurementListeners.forEach((listener) => listener());
}

function clampAspectRatio(ratio: number): number {
  return Math.min(
    MAX_MEDIA_ASPECT_RATIO,
    Math.max(MIN_MEDIA_ASPECT_RATIO, ratio)
  );
}

function measureMedia(
  naturalWidth: number,
  naturalHeight: number
): MediaMeasurement {
  return {
    aspectRatio: clampAspectRatio(naturalWidth / naturalHeight),
    isIcon:
      naturalWidth <= ICON_MAX_INTRINSIC_PX &&
      naturalHeight <= ICON_MAX_INTRINSIC_PX,
  };
}

/**
 * The frame takes its shape from the image itself.
 *
 * Adding an announcement image is deliberately a one-file change: set
 * `media_url` in `api_email_parser/lib/product_announcements.py` and stop.
 * There used to be a hardcoded media-URL -> aspect-ratio map here *and* an
 * identical one in the mobile app, so any new or repointed image needed three
 * files updated in lockstep. It silently went stale the first time it was
 * tested: #11024 repointed the all-inclusive announcement at a 2:1 asset and
 * left the map alone, so it reserved a 16/9 box and letterboxed the artwork
 * with nothing to catch it.
 *
 * Trade: the first view of an image settles from `RESERVED_MEDIA_ASPECT_RATIO`
 * to its true ratio once decoded, rather than being permanently wrong.
 */
function AnnouncementMedia({
  announcement,
  loading = "eager",
}: {
  announcement: ProductAnnouncement;
  /**
   * Older releases sit below the fold on open, so their artwork can defer
   * until scrolled near. The hero and newest article stay eager.
   */
  loading?: "eager" | "lazy";
}) {
  const measurement = useMediaMeasurement(announcement.mediaUrl);
  const [failed, setFailed] = useState(false);
  // A failed load has no measurement to trust, so it falls back to the
  // reserved artwork frame — the alt text needs the room.
  const isIcon = !failed && (measurement?.isIcon ?? false);

  return (
    <div
      data-testid={`product-announcement-media-frame-${announcement.id}`}
      data-media-kind={isIcon ? "icon" : "artwork"}
      className={isIcon ? "flex w-full justify-center" : "max-h-96 w-full"}
      // An icon takes a fixed size rather than deriving its height from its
      // own shape, which is what turned a 32px square into a 384px block.
      style={
        isIcon
          ? undefined
          : {
              aspectRatio:
                measurement?.aspectRatio ?? RESERVED_MEDIA_ASPECT_RATIO,
            }
      }
    >
      <div
        className={`flex items-center justify-center overflow-hidden rounded-xl ${
          isIcon ? "size-24" : "h-full"
        }`}
      >
        {failed ? (
          // Without this, a failed load leaves the reserved frame as an empty
          // grey box — indistinguishable from the bug this component was fixing.
          <p
            data-testid={`product-announcement-media-error-${announcement.id}`}
            className="flex h-full w-full items-center justify-center rounded-xl bg-muted px-6 text-center text-xs text-muted-foreground"
          >
            {announcement.mediaAltText}
          </p>
        ) : (
          /* Editorial media may be an animated GIF, so keep the native element. */
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={announcement.mediaUrl}
            alt={announcement.mediaAltText}
            loading={loading}
            className="h-full w-full object-contain"
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (!naturalWidth || !naturalHeight) return;
              recordMeasurement(
                announcement.mediaUrl,
                measureMedia(naturalWidth, naturalHeight)
              );
            }}
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Body paragraphs collapse behind "Read more" so a release reads as headline +
 * summary + artwork rather than a document. The full copy stays one click away.
 *
 * That trade only holds when the artwork carries some of the story. When the
 * image is just an icon there is nothing to read but the headline, so the
 * release opens expanded instead of hiding its whole substance behind a
 * control sitting at the bottom edge of the sheet.
 */
function AnnouncementBody({ item }: { item: ProductAnnouncement }) {
  const measurement = useMediaMeasurement(item.mediaUrl);
  // Null until the reader picks a side. The measurement lands after the image
  // decodes, so seeding `useState` with it would leave an icon-only release
  // collapsed on first paint and never reopen it.
  const [readerChoice, setReaderChoice] = useState<boolean | null>(null);
  const expanded = readerChoice ?? measurement?.isIcon ?? false;

  if (item.body.length === 0) return null;

  return (
    <div className="space-y-4">
      {expanded
        ? item.body.map((paragraph) => (
            <p
              key={paragraph}
              className="text-sm leading-relaxed text-muted-foreground"
            >
              {paragraph}
            </p>
          ))
        : null}
      <Button
        data-testid={`product-announcement-read-more-${item.id}`}
        variant="link"
        size="none"
        aria-expanded={expanded}
        className="gap-1 text-sm font-medium"
        onClick={() => setReaderChoice(!expanded)}
      >
        {expanded ? "Show less" : "Read more"}
        {expanded ? (
          <ChevronUp className="size-4" />
        ) : (
          <ChevronDown className="size-4" />
        )}
      </Button>
    </div>
  );
}

function releaseDateLabel(item: ProductAnnouncement): string {
  // `publishedAt` is a UTC-stamped instant from the generated client
  // (`new Date(json['published_at'])`), but the release date is a calendar
  // date. Formatting it locally moved the Gondola Cash release — published at
  // 2026-08-28T00:00Z — to "August 27, 2026" west of UTC, and forward a day
  // east of it. Must stay in sync with the mobile surface.
  return convertUTCToLocalDateIgnoringTimezone(
    item.publishedAt
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * The newest release's artwork, blurred, as the hero's full-bleed backdrop.
 * Keyed on `mediaUrl` at the call site so a repointed URL retries the load
 * rather than keeping a stale failure.
 */
function HeroBackdrop({ announcement }: { announcement: ProductAnnouncement }) {
  const measurement = useMediaMeasurement(announcement.mediaUrl);
  const [failed, setFailed] = useState(false);

  // `object-cover` on a UI icon is not a backdrop, it is a 32px asset
  // stretched over the full hero and blurred into a smear — what the Gondola
  // Cash release shipped. See ICON_MAX_INTRINSIC_PX: an icon carries no
  // editorial imagery to show, so the opaque surface the failed path already
  // uses is the honest hero for those releases. Must stay in sync with the
  // mobile surface.
  if (failed || measurement?.isIcon) {
    // The hero copy is pure-white on an always-dark surface; without the
    // artwork, an opaque dark layer keeps it legible in light mode.
    return (
      <div
        data-testid="product-announcement-hero-fallback"
        className="absolute inset-0 bg-pure-black"
      />
    );
  }

  return (
    /* Decorative: the same artwork renders legibly in the section below, so
       the blurred copy must not be announced twice. */
    /* eslint-disable-next-line @next/next/no-img-element */
    <img
      data-testid="product-announcement-hero-backdrop"
      src={announcement.mediaUrl}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
      // The hero paints before the inline frame decodes, so without its own
      // measurement an icon release would keep the smeared backdrop until
      // something else happened to load the same URL.
      onLoad={(event) => {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (!naturalWidth || !naturalHeight) return;
        recordMeasurement(
          announcement.mediaUrl,
          measureMedia(naturalWidth, naturalHeight)
        );
      }}
      onError={() => setFailed(true)}
    />
  );
}

function CategoryPill({ item }: { item: ProductAnnouncement }) {
  return (
    <Badge
      data-testid={`product-announcement-category-${item.id}`}
      variant="brand-gold"
      className="rounded-full px-3 py-1 capitalize"
    >
      {item.category}
    </Badge>
  );
}

export default function ProductAnnouncementsSurface({
  announcements,
  open,
  teaserVisible,
  onOpen,
  onOpenChange,
  onDismissTeaser,
}: Props) {
  const router = useRouter();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  // Set while a programmatic advance is animating. The smooth scroll's own
  // scroll events would recompute the pre-click index and flip the pill
  // backwards mid-flight, so the spy ignores events until the target reads
  // as active by geometry — with a timeout fallback for the case where the
  // user wheels away mid-animation and the target is never reached.
  const pendingTargetRef = useRef<number | null>(null);
  const pendingTargetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const announcement = announcements[0];

  useEffect(() => {
    if (!open) {
      setActiveIndex(0);
      pendingTargetRef.current = null;
    }
  }, [open]);

  useEffect(
    () => () => {
      if (pendingTargetTimerRef.current !== null) {
        clearTimeout(pendingTargetTimerRef.current);
      }
    },
    []
  );

  if (!announcement) return null;

  const sectionTop = (index: number): number => {
    const scroller = scrollerRef.current;
    const section = sectionRefs.current[index];
    if (!scroller || !section) return 0;
    return (
      section.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop
    );
  };

  const showRelease = (index: number) => {
    const nextIndex = Math.max(0, Math.min(index, announcements.length - 1));
    pendingTargetRef.current = nextIndex;
    if (pendingTargetTimerRef.current !== null) {
      clearTimeout(pendingTargetTimerRef.current);
    }
    // Smooth-scrolling these distances takes well under this; if the user
    // wheels away mid-animation the target may never read active, so the
    // suppression must not outlive the flight.
    pendingTargetTimerRef.current = setTimeout(() => {
      pendingTargetRef.current = null;
    }, 800);
    scrollerRef.current?.scrollTo?.({
      top: sectionTop(nextIndex),
      behavior:
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
    });
    setActiveIndex(nextIndex);
  };

  const atLastRelease = activeIndex >= announcements.length - 1;

  const advance = () => {
    if (atLastRelease) {
      onOpenChange(false);
      return;
    }
    showRelease(activeIndex + 1);
  };

  const syncActiveRelease = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let nextIndex = 0;
    // A last section shorter than the activation offset can never clear the
    // threshold walk below, so the scroll can rest at the very bottom while
    // the pill still reads "Next" — treat bottom-of-scroll as last-active.
    if (
      scroller.scrollTop + scroller.clientHeight >=
      scroller.scrollHeight - 1
    ) {
      nextIndex = announcements.length - 1;
    } else {
      const scrollerTop = scroller.getBoundingClientRect().top;
      sectionRefs.current.forEach((section, index) => {
        if (!section) return;
        // A section counts as active once its top clears the upper part of
        // the viewport, matching where the eye lands while scrolling.
        if (
          section.getBoundingClientRect().top - scrollerTop <=
          SECTION_ACTIVATION_OFFSET_PX
        ) {
          nextIndex = index;
        }
      });
    }
    // Mid-flight events from a programmatic advance are ignored until the
    // target section reads as active — otherwise the first frames of the
    // smooth scroll flip the pill back to the pre-click release.
    if (pendingTargetRef.current !== null) {
      if (nextIndex !== pendingTargetRef.current) return;
      pendingTargetRef.current = null;
      if (pendingTargetTimerRef.current !== null) {
        clearTimeout(pendingTargetTimerRef.current);
        pendingTargetTimerRef.current = null;
      }
    }
    setActiveIndex(nextIndex);
  };

  return (
    <>
      {teaserVisible ? (
        <aside className="fixed bottom-4 left-4 z-40 w-80 rounded-xl border border-border bg-card p-4 shadow-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2">
          <CloseButton
            size="sm"
            className="absolute right-2 top-2"
            ariaLabel="Dismiss product update"
            onClick={onDismissTeaser}
          />
          <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-3 whitespace-normal p-0 pr-6 text-left hover:bg-transparent"
            onClick={onOpen}
            aria-label={`Read what's new: ${announcement.title}`}
          >
            <span className="size-14 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
              {/* Editorial media may be an animated GIF, so keep the native element. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                data-testid="product-announcement-teaser-media"
                src={announcement.mediaUrl}
                alt=""
                aria-hidden="true"
                className="size-full object-cover object-left"
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-primary">
                New
              </span>
              <span className="block text-sm font-semibold text-foreground">
                {announcement.title}
              </span>
              <span className="mt-1 line-clamp-2 block text-xs text-muted-foreground">
                {announcement.summary}
              </span>
            </span>
          </Button>
        </aside>
      ) : null}

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          size="md"
          hideCloseButton
          className="flex h-[90dvh] max-h-[52rem] flex-col gap-0 overflow-hidden p-0"
        >
          <div className="relative min-h-0 flex-1">
            <div
              ref={scrollerRef}
              data-testid="product-announcement-scroll"
              className="h-full overflow-y-auto"
              onScroll={syncActiveRelease}
            >
              {/* Hero: the newest release's artwork as a full-bleed, blurred,
                  darkened backdrop with the headline set over it. The scrims
                  are pure-black because the hero is an always-dark surface in
                  both themes; copy on it is pure-white per the static-color
                  rules. */}
              <header
                ref={(element) => {
                  sectionRefs.current[0] = element;
                }}
                data-testid="product-announcement-hero"
                className="relative flex min-h-72 flex-col items-center justify-end overflow-hidden px-8 pb-10 pt-20 text-center"
              >
                <HeroBackdrop
                  key={announcement.mediaUrl}
                  announcement={announcement}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-pure-black/50 via-pure-black/25 to-pure-black/75" />
                <div className="relative flex flex-col items-center gap-3">
                  <div className="flex items-center gap-3">
                    <CategoryPill item={announcement} />
                    <time
                      dateTime={announcement.publishedAt.toISOString()}
                      className="text-xs text-pure-white/70"
                    >
                      {releaseDateLabel(announcement)}
                    </time>
                  </div>
                  <DialogTitle className="text-4xl font-bold leading-tight text-pure-white">
                    {announcement.title}
                  </DialogTitle>
                  <DialogDescription className="text-base leading-relaxed text-pure-white/80">
                    {announcement.summary}
                  </DialogDescription>
                </div>
              </header>

              {/* The newest release's legible artwork and details, directly
                  under its hero headline. */}
              <article
                data-testid={`product-announcement-${announcement.id}`}
                className="space-y-6 px-6 pt-6"
              >
                {/* Keyed on the URL, not the id: the measurement and the
                    failed flag are seeded once per mount, so repointing a
                    release's media_url under a stable id — exactly what #11024
                    did — would otherwise keep the old frame or a stale error
                    state. */}
                <AnnouncementMedia
                  key={announcement.mediaUrl}
                  announcement={announcement}
                />
                <AnnouncementBody item={announcement} />
                {announcement.ctaLabel && announcement.ctaHref ? (
                  <Button
                    className="w-fit"
                    onClick={() => {
                      router.push(announcement.ctaHref as string);
                      onOpenChange(false);
                    }}
                  >
                    {announcement.ctaLabel}
                    <ArrowRight className="size-4" />
                  </Button>
                ) : null}
              </article>

              <div className="space-y-12 px-6 pb-24 pt-12">
                {announcements.slice(1).map((item, sliceIndex) => {
                  // The list arrives newest-first. Every launch post is
                  // authored `new`, so rendering the category on all of them
                  // puts a "New" tag beside months-old entries — keep it on
                  // the newest only. Other categories (improved/fixed) stay:
                  // they describe the release, not its recency.
                  const showCategory =
                    item.category !== ProductAnnouncementCategoryEnum.New;

                  return (
                    <article
                      key={item.id}
                      ref={(element) => {
                        sectionRefs.current[sliceIndex + 1] = element;
                      }}
                      data-testid={`product-announcement-${item.id}`}
                      className="space-y-6"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          {showCategory ? <CategoryPill item={item} /> : null}
                          <time
                            dateTime={item.publishedAt.toISOString()}
                            className="text-xs text-muted-foreground"
                          >
                            {releaseDateLabel(item)}
                          </time>
                        </div>
                        <h3 className="text-2xl font-bold text-foreground">
                          {item.title}
                        </h3>
                        <p className="text-base leading-relaxed text-muted-foreground">
                          {item.summary}
                        </p>
                      </div>
                      <AnnouncementMedia
                        key={item.mediaUrl}
                        announcement={item}
                        loading="lazy"
                      />
                      <AnnouncementBody item={item} />
                      {item.ctaLabel && item.ctaHref ? (
                        <Button
                          className="w-fit"
                          onClick={() => {
                            router.push(item.ctaHref as string);
                            onOpenChange(false);
                          }}
                        >
                          {item.ctaLabel}
                          <ArrowRight className="size-4" />
                        </Button>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </div>

            <DialogClose asChild>
              <CloseButton
                size="sm"
                className="absolute right-4 top-4 z-10"
                iconContainerClassName="bg-pure-black/40 text-pure-white hover:bg-pure-black/55"
                ariaLabel="Close What's New"
              />
            </DialogClose>

            <Button
              data-testid="product-announcement-advance"
              variant="dark"
              aria-label={
                atLastRelease
                  ? "Done"
                  : `Next release, ${activeIndex + 2} of ${announcements.length}`
              }
              className="absolute bottom-4 right-4 z-10 h-11 gap-2 rounded-full px-6 shadow-lg"
              onClick={advance}
            >
              {atLastRelease ? "Done" : "Next"}
              {atLastRelease ? null : <ArrowRight className="size-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
